import { Type, type Static } from "@sinclair/typebox";
import { TripKind, TripStatus } from "./domain.ts";

/**
 * Trip sharing and trusted contacts — Compartilhar viagem (Cliente-18), Contatos de confiança
 * (Cliente-19), the public page (Cliente-20) and the driver's "Compartilhar rota ao vivo".
 */

export const MAX_TRUSTED_CONTACTS = 5;

export const TrustedContactView = Type.Object(
  { id: Type.String(), name: Type.String(), phoneMasked: Type.String({ description: "(11) •••••-4321" }), initials: Type.String() },
  { $id: "TrustedContactView" },
);
export type TrustedContactView = Static<typeof TrustedContactView>;
export const TrustedContactInput = Type.Object(
  { name: Type.String({ minLength: 1, maxLength: 60 }), phone: Type.String({ minLength: 10, maxLength: 20, description: "E.164 (+5511…) or a Brazilian number with DDD" }) },
  { $id: "TrustedContactInput" },
);

/** "Compartilhar automaticamente" (Cliente-19). Defaults: always false, night/deviation/arrival true. */
export const ShareRules = Type.Object(
  {
    /** "Em toda viagem" — send the link as soon as the driver accepts. */
    always: Type.Boolean(),
    /** "À noite" — trips accepted between 22h and 6h (city time). */
    night: Type.Boolean(),
    /** "Se a rota desviar" — driver > 500 m off the route for > 60 s: text contacts once, push the rider. */
    deviation: Type.Boolean(),
    /** "Avisar quando eu chegar" — text "{nome} chegou ao destino" when the trip ends. */
    arrival: Type.Boolean(),
  },
  { $id: "ShareRules" },
);
export type ShareRules = Static<typeof ShareRules>;

export const TripShareRequest = Type.Object({ contactIds: Type.Array(Type.String(), { maxItems: 5 }) }, { $id: "TripShareRequest" });
export const TripShareView = Type.Object(
  {
    token: Type.String(),
    url: Type.String({ description: "PUBLIC_TRIP_URL with the token, e.g. https://v.ichegoubr.com/{token}" }),
    contacts: Type.Array(Type.Object({ id: Type.String(), name: Type.String() })),
    active: Type.Boolean(),
  },
  { $id: "TripShareView" },
);
export type TripShareView = Static<typeof TripShareView>;

export const DeliveryStepId = Type.Union(
  [Type.Literal("confirmed"), Type.Literal("picked_up"), Type.Literal("in_transit"), Type.Literal("delivered")],
  { $id: "DeliveryStepId" },
);
/** Confirmado / Coletado / A caminho / Entregue, with the time each happened (null = not yet). */
export const DeliveryStep = Type.Object(
  { id: DeliveryStepId, title: Type.String(), at: Type.Union([Type.String({ format: "date-time" }), Type.Null()]) },
  { $id: "DeliveryStep" },
);

/** `GET /public/trips/{token}` (no auth). 410 `{ error: "ended" }` once the share stopped or expired. */
export const PublicTripView = Type.Object(
  {
    status: TripStatus,
    kind: TripKind,
    riderFirstName: Type.String(),
    driver: Type.Union([
      Type.Object({
        /** "Marcos S." */
        firstName: Type.String(),
        rating: Type.Number(),
        /** A short-lived link; absent without a photo. */
        photoUrl: Type.Optional(Type.String()),
        /** The blue check: the latest prova de vida video was approved. */
        identityVerified: Type.Optional(Type.Boolean()),
        vehicle: Type.Object({
          model: Type.String(), color: Type.String(), plate: Type.String(),
          kind: Type.Optional(Type.Union([Type.Literal("car"), Type.Literal("motorcycle")])),
        }),
      }),
      Type.Null(),
    ]),
    pickup: Type.Object({ title: Type.String() }),
    dropoff: Type.Object({ title: Type.String() }),
    /** To the pickup before boarding, to the dropoff on board. */
    etaSeconds: Type.Union([Type.Integer(), Type.Null()]),
    arrivesAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    polyline: Type.Union([Type.String(), Type.Null()]),
    driverLocation: Type.Union([Type.Object({ lat: Type.Number(), lng: Type.Number(), heading: Type.Union([Type.Number(), Type.Null()]) }), Type.Null()]),
    /** "Embarcou às 00:31". */
    startedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    /** "rota dentro do previsto" / route deviation detected. Null before boarding. */
    routeStatus: Type.Union([Type.Literal("on_route"), Type.Literal("deviated"), Type.Null()]),
    updatedAt: Type.String({ format: "date-time" }),
    deliverySteps: Type.Optional(Type.Array(DeliveryStep)),
    /** Paradas, in order; `done` once the driver left it. */
    stops: Type.Optional(Type.Array(Type.Object({ title: Type.String(), done: Type.Boolean() }))),
  },
  { $id: "PublicTripView" },
);
export type PublicTripView = Static<typeof PublicTripView>;
